import { MinusCircleIcon, PencilIcon } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import employersEmployees from 'rails_routes/Employers/EmployeesPaths'
import { Badge, Button, Checkbox, Table, TextLink, useBreakpoint } from 'slab'
import { usePostEmployersOffboardingBulkOffboard } from 'utils/web_api'

import { type EmployeeOffboardingData, useBulkOffboarding } from './BulkOffboardingContext'
import { COBRA_EMPLOYEE_THRESHOLD } from './constants'

interface SubmitStepProps {
  employerName?: string | null
  employerDba?: string | null
  isAle?: boolean | null
}

function CobraBadge ({ cobraEligible }: { cobraEligible: boolean | null }) {
  if (cobraEligible === true) return <Badge type='success'>Yes</Badge>
  if (cobraEligible === false) return <Badge type='neutral'>No</Badge>
  return <span className='text-gray-900'>—</span>
}

function CobraConfirmation ({
  checked,
  onChange,
  displayEmployerName,
  currentYear,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  displayEmployerName: string
  currentYear: number
}) {
  return (
    <Checkbox
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      label={`I confirm that ${displayEmployerName} had at least ${COBRA_EMPLOYEE_THRESHOLD} employees on most typical days of ${currentYear}.`}
      description={
        <>
          For more information, see the{' '}
          <TextLink
            href='https://www.dol.gov/agencies/ebsa/laws-and-regulations/laws/cobra'
            target='_blank'
            rel='noopener noreferrer'
          >
            federal COBRA guidelines
          </TextLink>
          .
        </>
      }
    />
  )
}

function SubmitActions ({
  onBack,
  onConfirm,
  isPending,
  disabled,
  employeeCount,
  layout,
}: {
  onBack: () => void
  onConfirm: () => void
  isPending: boolean
  disabled: boolean
  employeeCount: number
  layout: 'row' | 'stack'
}) {
  return (
    <div className={layout === 'stack' ? 'flex flex-col-reverse gap-3' : 'flex gap-3 justify-end'}>
      <Button
        variant='secondary'
        size='large'
        width={layout === 'stack' ? 'full' : undefined}
        onClick={onBack}
        disabled={isPending}
      >
        Back
      </Button>
      <Button
        variant='primary-destructive'
        size='large'
        width={layout === 'stack' ? 'full' : undefined}
        onClick={onConfirm}
        disabled={disabled}
        leftIcon={MinusCircleIcon}
      >
        {isPending ? 'Offboarding...' : `Offboard ${employeeCount} employee${employeeCount === 1 ? '' : 's'}`}
      </Button>
    </div>
  )
}

function formatDate (dateStr: string): string {
  return new Date(dateStr).toLocaleDateString()
}

export function SubmitStep ({ employerName, employerDba, isAle }: SubmitStepProps) {
  const {
    getSelectedEmployees,
    employeeOffboardingData,
    setCurrentStep,
    setCurrentEmployeeIndex,
  } = useBulkOffboarding()

  const [confirmationChecked, setConfirmationChecked] = useState(false)
  const { breakpointIsAtLeast } = useBreakpoint()
  const isDesktop = breakpointIsAtLeast('md')

  const selectedEmployees = getSelectedEmployees()
  const employeeCount = selectedEmployees.length

  // Check if any employee is COBRA eligible
  const hasCobraEligibleEmployees = selectedEmployees.some(employee => {
    const data = employeeOffboardingData.get(employee.id)
    return data?.cobraEligible === true
  })

  // Only show COBRA confirmation checkbox if there are COBRA eligible employees AND employer is not an ALE
  // ALEs (Applicable Large Employers with 50+ employees) don't need to confirm the 20-employee threshold
  const showCobraConfirmation = hasCobraEligibleEmployees && !isAle

  const bulkOffboardMutation = usePostEmployersOffboardingBulkOffboard()

  const handleBack = () => {
    setCurrentEmployeeIndex(selectedEmployees.length - 1)
    setCurrentStep('details')
  }

  const displayEmployerName = employerDba || employerName || 'my organization'
  const currentYear = new Date().getFullYear()

  const handleEditEmployee = (employeeId: string) => {
    const employeeIndex = selectedEmployees.findIndex(e => e.id === employeeId)
    if (employeeIndex !== -1) {
      setCurrentEmployeeIndex(employeeIndex)
      setCurrentStep('details')
    }
  }

  const handleConfirm = () => {
    const employees = selectedEmployees.map(employee => {
      const data = employeeOffboardingData.get(employee.id)
      return {
        employee_id: employee.id,
        effective_date: data?.finalDayOfEmployment ?? '',
        cobra_eligible: data?.cobraEligible ?? false,
        employee_personal_email: data?.personalEmail ?? '',
      }
    })

    bulkOffboardMutation.mutate(
      { data: { employees } },
      {
        onSuccess: () => {
          window.location.href = employersEmployees.index()
        },
        onError: () => {
          toast.error('We ran into an error offboarding these employees. Please try again, or contact support if the problem persists.')
        },
      }
    )
  }

  const isSubmitDisabled = bulkOffboardMutation.isPending || (showCobraConfirmation && !confirmationChecked)

  const renderEditButton = (employeeId: string, employeeName: string, size: number = 16) => (
    <button
      onClick={() => handleEditEmployee(employeeId)}
      className='text-gray-500 hover:text-gray-700 flex items-center'
      aria-label={`Edit ${employeeName}`}
    >
      <PencilIcon size={size} />
    </button>
  )

  const renderEmployeeData = (data: EmployeeOffboardingData | undefined) => ({
    finalDay: data?.finalDayOfEmployment ? formatDate(data.finalDayOfEmployment) : '—',
    personalEmail: data?.personalEmail || '—',
    cobraEligible: data?.cobraEligible ?? null,
  })

  return (
    <div>
      {/* Page header */}
      <div className='mb-6'>
        <h1 className='heading-lg'>Review and confirm</h1>
        <p className='body-sm text-gray-500 mt-2'>
          After you submit, you'll have up until each employee's final day of employment to make changes.
        </p>
      </div>

      {/* Summary - Table on desktop, Cards on mobile */}
      {isDesktop
        ? (
          <>
            <div className='bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-6'>
              <Table width='full'>
                <Table.Head>
                  <Table.Row>
                    <Table.ColumnHeader>Name</Table.ColumnHeader>
                    <Table.ColumnHeader>Final day of employment</Table.ColumnHeader>
                    <Table.ColumnHeader>COBRA eligible</Table.ColumnHeader>
                    <Table.ColumnHeader>Personal email</Table.ColumnHeader>
                    <Table.ColumnHeader> </Table.ColumnHeader>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {selectedEmployees.map(employee => {
                    const { finalDay, personalEmail, cobraEligible } = renderEmployeeData(employeeOffboardingData.get(employee.id))
                    return (
                      <Table.Row key={employee.id}>
                        <Table.Cell>
                          {employee.first_name} {employee.last_name}
                        </Table.Cell>
                        <Table.Cell>{finalDay}</Table.Cell>
                        <Table.Cell>
                          <CobraBadge cobraEligible={cobraEligible} />
                        </Table.Cell>
                        <Table.Cell>{personalEmail}</Table.Cell>
                        <Table.Cell>
                          {renderEditButton(employee.id, `${employee.first_name} ${employee.last_name}`)}
                        </Table.Cell>
                      </Table.Row>
                    )
                  })}
                </Table.Body>
              </Table>
            </div>

            {showCobraConfirmation && (
              <div className='mb-6'>
                <CobraConfirmation
                  checked={confirmationChecked}
                  onChange={setConfirmationChecked}
                  displayEmployerName={displayEmployerName}
                  currentYear={currentYear}
                />
              </div>
            )}

            <SubmitActions
              onBack={handleBack}
              onConfirm={handleConfirm}
              isPending={bulkOffboardMutation.isPending}
              disabled={isSubmitDisabled}
              employeeCount={employeeCount}
              layout='row'
            />
          </>
          )
        : (
          <div className='bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden'>
            {selectedEmployees.map((employee, index) => {
              const { finalDay, cobraEligible } = renderEmployeeData(employeeOffboardingData.get(employee.id))
              return (
                <div
                  key={employee.id}
                  className={`px-4 py-4 ${index < selectedEmployees.length - 1 ? 'border-b border-gray-200' : ''}`}
                >
                  <div className='flex items-start justify-between mb-4'>
                    <div>
                      <p className='body-bold'>
                        {employee.first_name} {employee.last_name}
                      </p>
                      <p className='body-sm text-gray-500'>
                        {employee.personal_email || 'No email on file'}
                      </p>
                    </div>
                    <button
                      onClick={() => handleEditEmployee(employee.id)}
                      className='text-gray-500 hover:text-gray-700 p-1 -mr-1'
                      aria-label={`Edit ${employee.first_name} ${employee.last_name}`}
                    >
                      <PencilIcon size={20} />
                    </button>
                  </div>

                  <div className='flex flex-col gap-1'>
                    <div className='flex justify-between body-sm'>
                      <span className='text-gray-500'>Final day of employment</span>
                      <span className='text-gray-900'>{finalDay}</span>
                    </div>
                    <div className='flex justify-between items-center body-sm'>
                      <span className='text-gray-500'>COBRA eligible</span>
                      <CobraBadge cobraEligible={cobraEligible} />
                    </div>
                    <div className='flex justify-between gap-4 body-sm'>
                      <span className='text-gray-500 whitespace-nowrap'>Personal email</span>
                      <span className='text-gray-900 truncate min-w-0'>
                        {employee.personal_email || '—'}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}

            <div className='border-t border-gray-200 px-4 py-4'>
              {showCobraConfirmation && (
                <div className='mb-4'>
                  <CobraConfirmation
                    checked={confirmationChecked}
                    onChange={setConfirmationChecked}
                    displayEmployerName={displayEmployerName}
                    currentYear={currentYear}
                  />
                </div>
              )}

              <SubmitActions
                onBack={handleBack}
                onConfirm={handleConfirm}
                isPending={bulkOffboardMutation.isPending}
                disabled={isSubmitDisabled}
                employeeCount={employeeCount}
                layout='stack'
              />
            </div>
          </div>
          )}
    </div>
  )
}
